import { useScrollLock } from "@/hooks/useScrollLock";
import { X } from "@phosphor-icons/react";
import { useEffect, type ReactNode } from "react";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  side?: "left" | "right";
  title?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Slide-in drawer panel with backdrop, ESC to close, and scroll lock.
 */
export function Drawer({
  open,
  onClose,
  side = "right",
  title,
  children,
  className = "",
}: DrawerProps) {
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const positionClass =
    side === "left"
      ? "left-0 animate-slide-in-left"
      : "right-0 animate-slide-in-right";

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`relative ${positionClass} h-full w-80 max-w-[85vw] bg-bg-card border-${side === "left" ? "r" : "l"} border-border shadow-xl flex flex-col ${className}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          {title && (
            <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
          )}
          <button
            onClick={onClose}
            className="ml-auto p-1.5 rounded-lg text-text-muted hover:bg-bg-surface transition-colors"
            aria-label="Close drawer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
